import type { UserSummary } from "./api";
import type { PickOption } from "./search";

export type PersonOption = PickOption;

/** LiteLLM's placeholder user: the administrator account's own keys (see Administration › Keys). */
const ADMIN_USER = "default_user_id";

/** How a request's person is shown on Administration › Requests: the administrator account by name. */
export function personLabel(userId: string): string {
  return userId === ADMIN_USER ? "Corporate and AI (administrator)" : userId;
}

/**
 * Everyone whose requests can carry a person, for the Person filter on Administration › Requests.
 * Not the app's own service users (svc-*), which make no requests of their own; service keys
 * have no person at all, so they cannot be chosen here.
 */
export function personOptions(users: Pick<UserSummary, "userId" | "email">[]): PersonOption[] {
  return users
    .filter((u) => !u.userId.startsWith("svc-"))
    .map((u) => ({ value: u.userId, label: u.userId === ADMIN_USER ? personLabel(u.userId) : u.email ?? u.userId }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Who may own a service key, for the Owner and Backup owner pickers (TAI-2567): people only, not
 * LiteLLM's placeholder user or the app's own service users, and never `exclude` (the other owner).
 * `keep`, the owner already recorded, stays listed even when it is no longer a user, so editing an
 * older key shows its owner rather than a blank field.
 */
export function ownerOptions(users: Pick<UserSummary, "userId" | "email">[], exclude = "", keep = ""): PersonOption[] {
  const out = users
    .filter((u) => u.userId !== ADMIN_USER && !u.userId.startsWith("svc-") && u.userId !== exclude)
    .map((u) => ({ value: u.userId, label: u.email ?? u.userId }));
  if (keep && keep !== exclude && !out.some((o) => o.value === keep)) out.push({ value: keep, label: keep });
  return out.sort((a, b) => a.label.localeCompare(b.label));
}
